/**
 * P4-06: QueuePanel —— 队列面板。
 *
 * 取代 `js/jsx/queue.jsx.js:109-255` 的 `WQueue`：
 * - 读取 `game.time.queue` 的 queueSourcesArr / queueItems / alphabeticalSort / cap；
 * - 类型下拉、项目下拉、Add to queue 按钮；
 * - 字母排序开关；
 * - 渲染 QueueItem 列表。
 *
 * 被 `getFeatureFlag("QUEUE")` 开关控制显示（由父层 RightPanel 决定）。
 */
import { useCallback, useMemo, useState } from "react";
import { useGameTick } from "../../bridge/useGameTick";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import QueueItem, { type QueueItemData } from "./QueueItem";

interface QueueSource {
  name: string;
  label: string;
}

interface QueueManagerLike {
  queueSourcesArr: QueueSource[];
  queueItems: QueueItemData[];
  alphabeticalSort: boolean;
  cap: number;
  queueLength(): number;
  getQueueOptions(typeId: string): { name: string; label: string }[];
  addToQueue(name: string, typeId: string, label: string, event: React.MouseEvent): void;
  toggleAlphabeticalSort(): void;
  remove(index: number, count: number): void;
  pushBack(index: number): void;
  pushFront(index: number): void;
  getQueueElementControllerAndModel(
    item: QueueItemData,
  ): { controller: unknown; model: unknown } | null;
  getQueueElementModel(item: QueueItemData): { prices?: { name: string; val: number }[] } | null;
}

interface QueueSnapshot {
  queueManager: QueueManagerLike;
  queueSources: QueueSource[];
  queueItems: QueueItemData[];
  alphabeticalSort: boolean;
  cap: number;
  queueLength: number;
}

function getQueueSnapshot(game: GamePage | null): QueueSnapshot | null {
  if (!game?.time?.queue) {
    return null;
  }
  const qm = game.time.queue as QueueManagerLike;
  return {
    queueManager: qm,
    // 引擎返回可变数组；拷贝以避免 tick 间突变污染 useGameTick 缓存比较。
    queueSources: [...qm.queueSourcesArr],
    queueItems: [...qm.queueItems],
    alphabeticalSort: qm.alphabeticalSort,
    cap: qm.cap,
    queueLength: qm.queueLength(),
  };
}

function snapEq(a: QueueSnapshot | null, b: QueueSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (
    a.alphabeticalSort !== b.alphabeticalSort ||
    a.cap !== b.cap ||
    a.queueLength !== b.queueLength ||
    a.queueSources.length !== b.queueSources.length ||
    a.queueItems.length !== b.queueItems.length
  ) {
    return false;
  }
  for (let i = 0; i < a.queueSources.length; i++) {
    if (
      a.queueSources[i].name !== b.queueSources[i].name ||
      a.queueSources[i].label !== b.queueSources[i].label
    ) {
      return false;
    }
  }
  for (let i = 0; i < a.queueItems.length; i++) {
    const ia = a.queueItems[i];
    const ib = b.queueItems[i];
    if (!ia || !ib) {
      return false;
    }
    if (
      ia.name !== ib.name ||
      ia.type !== ib.type ||
      ia.label !== ib.label ||
      ia.value !== ib.value
    ) {
      return false;
    }
  }
  return true;
}

export default function QueuePanel() {
  const selector = useCallback(
    (game: GamePage | null) => getQueueSnapshot(game),
    [],
  );
  const snapshot = useGameTick(selector, snapEq);
  const [typeId, setTypeId] = useState("buildings");
  const [itemIndex, setItemIndex] = useState(0);

  const options = useMemo(() => {
    if (!snapshot) {
      return [];
    }
    return snapshot.queueManager.getQueueOptions(typeId);
  }, [snapshot, typeId]);

  if (!snapshot) {
    return <div id="queueViewport" className="queue-container" />;
  }

  const handleAdd = (e: React.MouseEvent) => {
    const option = options[itemIndex];
    if (!option) {
      return;
    }
    snapshot.queueManager.addToQueue(option.name, typeId, option.label, e);
  };

  return (
    <div id="queueViewport" className="queue-container">
      <select
        value={typeId}
        onChange={(e) => {
          const nextType = e.target.value;
          setTypeId(nextType);
          const nextOptions = snapshot.queueManager.getQueueOptions(nextType);
          setItemIndex(nextOptions.length ? 0 : -1);
        }}
      >
        {snapshot.queueSources.map((source) => (
          <option key={source.name} value={source.name}>{source.label}</option>
        ))}
      </select>
      <select
        value={itemIndex}
        onChange={(e) => setItemIndex(Number(e.target.value))}
      >
        {options.length ? (
          options.map((opt, i) => (
            <option key={opt.name} value={i}>{opt.label}</option>
          ))
        ) : (
          <option value={-1}>-</option>
        )}
      </select>
      <button onClick={handleAdd}>{t("queue.add")}</button>

      <div className="alphabetical-toggle">
        <input
          id="queueAlphabetical"
          type="checkbox"
          checked={snapshot.alphabeticalSort}
          onChange={() => snapshot.queueManager.toggleAlphabeticalSort()}
          style={{ display: "inline-block" }}
        />
        <label htmlFor="queueAlphabetical">{t("queue.alphabeticalToggle")}</label>
      </div>

      <div className="queue-items">
        {snapshot.queueItems.map((item, index) => {
          if (!item) {
            return <div key={`unknown-${index}`}>&lt;unknown&gt;</div>;
          }
          return (
            <QueueItem
              key={`${item.name}-${index}`}
              item={item}
              index={index}
              isLast={index === snapshot.queueItems.length - 1}
              game={getGame() as GamePage}
              queueManager={snapshot.queueManager}
            />
          );
        })}
      </div>
    </div>
  );
}
